import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePostAggregatePizzas } from "@/api/pizza-checker/pizzas/pizzas";
import { useDashboardDateRange } from "@/context/dashboard-date-range-context";
import { subDays } from "date-fns";
import { analyzeDatePeriod } from "@/lib/date-period-utils";
import { useBranchFilter } from "@/hooks/use-branch-filter";
import { getTrendBadgeColor } from "@/lib/badge-utils";
import { ImageCaptureQualityCard } from "@/components/image-capture-quality-card";
import { useLocalization } from "@/context/localization-context";
import { buildScorePipeline, calculateScore } from "@/lib/branch-score";

type StatData = {
  title: string;
  value: string;
  trend?: "up" | "down" | "none";
  percentage?: string;
  trendText?: string;
  subValue?: string;
  subText?: string;
  metricType: "good-up" | "good-down" | "neutral";
  tooltipRows?: { label: string; value: string }[];
};

type AggregateStats = {
  totalCaptures: number;
  noPizza: number;
  evaluatable: number;
  allNok: number;
  allNokNotRecaptured: number;
};

const readCount = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const readFirstRow = (result: unknown): Record<string, unknown> =>
  (result as { results?: Record<string, unknown>[] })?.results?.[0] ?? {};

/** Branch quality score of the whole selection (same formula as the branch scoring table). */
const readScore = (result: unknown) => {
  const row = readFirstRow(result);
  const totalImages = readCount(row.totalImages);
  const totalPenalty = readCount(row.totalPenalty);

  return {
    totalImages,
    totalPenalty,
    score: calculateScore(totalImages, totalPenalty),
  };
};

function StatCard({ stat }: { stat: StatData }) {
  let trendColors = {
    background: "bg-muted border-border",
    text: "text-muted-foreground",
  };

  if (
    stat.trend &&
    stat.trend !== "none" &&
    stat.metricType !== "neutral"
  ) {
    trendColors = getTrendBadgeColor(stat.trend, stat.metricType);
  }

  const card = (
    <Card
      className={`@container/card bg-card border shadow-sm ${trendColors.background
        .replace("bg-", "border-")
        .replace("-100", "-200")}`}
    >
      <CardHeader className="relative pb-2">
        <CardDescription className="text-sm font-medium text-muted-foreground">
          {stat.title}
        </CardDescription>
        <CardTitle className="mt-1 text-3xl font-bold tabular-nums tracking-tight @[250px]/card:text-4xl">
          {stat.value}
        </CardTitle>
      </CardHeader>
      <CardFooter className="flex flex-col items-start gap-1 pt-0">
        {stat.percentage && stat.trendText && (
          <div className="flex items-center gap-1 text-sm font-medium">
            <span
              className={
                (stat.trend === "up" && stat.metricType === "good-up") ||
                (stat.trend === "down" && stat.metricType === "good-down")
                  ? "text-green-600"
                  : "text-red-600"
              }
            >
              {stat.percentage}
            </span>
            <span className="text-muted-foreground">{stat.trendText}</span>
          </div>
        )}
        {!stat.percentage && stat.subValue && stat.subText && (
          <div className="flex items-center gap-1 text-sm font-medium">
            <span>{stat.subValue}</span>
            <span className="text-muted-foreground">{stat.subText}</span>
          </div>
        )}
        {!stat.percentage && !stat.subValue && stat.subText && (
          <div className="text-sm text-muted-foreground">{stat.subText}</div>
        )}
      </CardFooter>
    </Card>
  );

  if (!stat.tooltipRows?.length) {
    return card;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{card}</TooltipTrigger>
        <TooltipContent className="space-y-1 bg-popover text-popover-foreground">
          {stat.tooltipRows.map((row) => (
            <div key={row.label} className="flex min-w-44 justify-between gap-4">
              <span className="text-muted-foreground">{row.label}</span>
              <span className="font-medium">{row.value}</span>
            </div>
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function SectionCards() {
  const [statsData, setStatsData] = useState<StatData[]>([]);
  const { processedDateRange } = useDashboardDateRange();
  const { t, formatNumber } = useLocalization();

  const formatCount = (value: number) => formatNumber(value);
  /** `value` in percent (58.1 -> "58.1%" / "58,1 %") */
  const formatPercent = (value: number, fractionDigits = 0) =>
    formatNumber(value / 100, {
      style: "percent",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  const { start: currStart, end: currEnd } = useMemo(() => {
    if (processedDateRange.start && processedDateRange.end) {
      return { start: processedDateRange.start, end: processedDateRange.end };
    }
    const end = new Date();
    const start = subDays(end, 7);
    return { start, end };
  }, [processedDateRange]);

  const periodInfo = useMemo(
    () => analyzeDatePeriod(currStart, currEnd),
    [currStart, currEnd],
  );

  const { start: prevStart, end: prevEnd } = useMemo(
    () => periodInfo.previousPeriod,
    [periodInfo],
  );

  const branchFilter = useBranchFilter();
  const { mutateAsync } = usePostAggregatePizzas({});

  useEffect(() => {
    let isMounted = true;

    async function fetchStats() {
      const classifiedPizzaCondition = {
        $and: [
          { $ne: ["$evaluation.metadata.classification", "No Pizza"] },
          {
            $ne: [
              { $type: "$evaluation.metadata.classification" },
              "missing",
            ],
          },
          { $ne: ["$evaluation.metadata.classification", null] },
        ],
      };

      const evaluatableCondition = {
        $and: [
          classifiedPizzaCondition,
          {
            $or: [
              {
                $eq: [
                  "$evaluation.metadata.photo_quality_evaluation.status",
                  "OK",
                ],
              },
              {
                $eq: [
                  {
                    $type:
                      "$evaluation.metadata.photo_quality_evaluation.status",
                  },
                  "missing",
                ],
              },
              {
                $eq: [
                  "$evaluation.metadata.photo_quality_evaluation.status",
                  null,
                ],
              },
            ],
          },
        ],
      };

      const allNokCondition = {
        $and: [
          classifiedPizzaCondition,
          { $ne: ["$evaluation.metadata.classification", "OK Pizza"] },
        ],
      };

      const pipeline = (start: Date, end: Date) => [
        {
          $match: {
            capture_at: {
              $gte: start.toISOString(),
              $lte: end.toISOString(),
            },
            ...branchFilter,
          },
        },
        {
          $group: {
            _id: null,
            totalCaptures: { $sum: 1 },
            noPizza: {
              $sum: {
                $cond: [
                  { $eq: ["$evaluation.metadata.classification", "No Pizza"] },
                  1,
                  0,
                ],
              },
            },
            evaluatable: {
              $sum: {
                $cond: [evaluatableCondition, 1, 0],
              },
            },
            allNok: {
              $sum: {
                $cond: [allNokCondition, 1, 0],
              },
            },
            allNokNotRecaptured: {
              $sum: {
                $cond: [
                  {
                    $and: [
                      allNokCondition,
                      { $lte: [{ $ifNull: ["$capture_count", 0] }, 1] },
                    ],
                  },
                  1,
                  0,
                ],
              },
            },
          },
        },
      ];

      try {
        const [currResponse, prevResponse, currScoreResponse, prevScoreResponse] =
          await Promise.all([
            mutateAsync({ data: { pipeline: pipeline(currStart, currEnd) } }),
            mutateAsync({ data: { pipeline: pipeline(prevStart, prevEnd) } }),
            mutateAsync({
              data: {
                pipeline: buildScorePipeline(
                  currStart,
                  currEnd,
                  branchFilter,
                  null,
                ),
              },
            }),
            mutateAsync({
              data: {
                pipeline: buildScorePipeline(
                  prevStart,
                  prevEnd,
                  branchFilter,
                  null,
                ),
              },
            }),
          ]);

        if (!isMounted) return;

        const readStats = (result: unknown): AggregateStats => {
          const row = readFirstRow(result);

          return {
            totalCaptures: readCount(row.totalCaptures),
            noPizza: readCount(row.noPizza),
            evaluatable: readCount(row.evaluatable),
            allNok: readCount(row.allNok),
            allNokNotRecaptured: readCount(row.allNokNotRecaptured),
          };
        };

        const curr = readStats(currResponse);
        const prev = readStats(prevResponse);

        const currScore = readScore(currScoreResponse);
        const prevScore = readScore(prevScoreResponse);
        const scoreTrend =
          currScore.score !== null && prevScore.score !== null
            ? currScore.score - prevScore.score
            : null;

        const currEvaluatableRate =
          curr.totalCaptures > 0
            ? (curr.evaluatable / curr.totalCaptures) * 100
            : 0;
        const obstacleCount = Math.max(
          curr.totalCaptures - curr.noPizza - curr.evaluatable,
          0,
        );

        const currNokNoRetakeRate =
          curr.allNok > 0
            ? (curr.allNokNotRecaptured / curr.allNok) * 100
            : 0;
        const prevNokNoRetakeRate =
          prev.allNok > 0
            ? (prev.allNokNotRecaptured / prev.allNok) * 100
            : 0;
        const nokNoRetakeTrend = currNokNoRetakeRate - prevNokNoRetakeRate;

        const trendTextLabel = t("stats.vsPreviousPeriod");

        const stats: StatData[] = [
          {
            title: t("stats.totalScore"),
            value:
              currScore.score === null
                ? t("common.noData")
                : formatPercent(currScore.score, 1),
            ...(scoreTrend !== null && {
              trend: scoreTrend >= 0 ? ("up" as const) : ("down" as const),
              percentage: `${scoreTrend >= 0 ? "+" : ""}${formatPercent(
                scoreTrend,
                1,
              )}`,
              trendText: trendTextLabel,
            }),
            metricType: "good-up",
            tooltipRows: [
              {
                label: t("stats.checkedPizzas"),
                value: formatCount(currScore.totalImages),
              },
              {
                label: t("stats.penaltyPoints"),
                value: formatCount(currScore.totalPenalty),
              },
            ],
          },
          {
            title: t("stats.nokNoRetake"),
            value: formatPercent(currNokNoRetakeRate, 1),
            trend: nokNoRetakeTrend <= 0 ? "down" : "up",
            percentage: `${nokNoRetakeTrend >= 0 ? "+" : ""}${formatPercent(
              nokNoRetakeTrend,
              1,
            )}`,
            trendText: trendTextLabel,
            metricType: "good-down",
            tooltipRows: [
              {
                label: t("stats.nokNoRetake"),
                value: formatCount(curr.allNokNotRecaptured),
              },
              {
                label: t("stats.nokPizzas"),
                value: formatCount(curr.allNok),
              },
            ],
          },
          {
            title: t("stats.evaluableImages"),
            value: formatPercent(Math.round(currEvaluatableRate)),
            subText: t("stats.target", { value: formatPercent(98) }),
            metricType: "good-up",
            tooltipRows: [
              {
                label: t("captureQuality.evaluable"),
                value: formatCount(curr.evaluatable),
              },
              {
                label: t("captureQuality.obstacle"),
                value: formatCount(obstacleCount),
              },
              {
                label: t("captureQuality.noPizza"),
                value: formatCount(curr.noPizza),
              },
            ],
          },
        ];

        setStatsData(stats);
      } catch (error) {
        console.error("Failed to fetch stats", error);
      }
    }
    fetchStats();

    return () => {
      isMounted = false;
    };
  }, [
    currStart,
    currEnd,
    prevStart,
    prevEnd,
    mutateAsync,
    periodInfo,
    branchFilter,
    formatNumber,
    t,
  ]);

  return (
    <div className="flex flex-col gap-4 px-4 lg:px-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {statsData.map((stat) => (
          <StatCard key={stat.title} stat={stat} />
        ))}
      </div>
      <ImageCaptureQualityCard />
    </div>
  );
}
